import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { maintenanceAPI } from '../services/api';

/**
 * Maintenance Page - Shown when system is under maintenance.
 * Centered, clean, responsive design.
 * Auto-redirects back to dashboard when maintenance ends.
 * Uses server_time for accurate ETA.
 */
const Maintenance = () => {
  const navigate = useNavigate();
  const [info, setInfo] = useState(() => {
    try {
      const stored = sessionStorage.getItem('maintenance_info');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [eta, setEta] = useState('');
  const [maintenanceEnded, setMaintenanceEnded] = useState(false);
  const serverOffsetRef = useRef(0);
  const redirectingRef = useRef(false);

  const checkStatus = useCallback(async () => {
    // Guard: don't process if already transitioning
    if (redirectingRef.current) return;

    try {
      const beforeMs = Date.now();
      const res = await maintenanceAPI.getStatus();
      const afterMs = Date.now();

      if (res.data?.success) {
        const data = res.data.data;

        // Update server time offset
        if (data.server_time) {
          const serverMs = new Date(data.server_time).getTime();
          serverOffsetRef.current = serverMs - (beforeMs + afterMs) / 2;
        }

        const ended = (!data.maintenance_mode && !data.schedule) ||
          data.schedule?.status === 'completed' ||
          (data.schedule && !data.schedule.is_active);

        if (ended) {
          redirectingRef.current = true;
          sessionStorage.removeItem('maintenance_info');
          setMaintenanceEnded(true);
          setTimeout(() => navigate('/dashboard'), 1500);
          return;
        }

        if (data.schedule) {
          setInfo(data.schedule);
        }
      }
    } catch {
      // silently fail
    }
  }, [navigate]);

  // Poll every 15 seconds to detect when maintenance ends
  useEffect(() => {
    checkStatus();
    const interval = setInterval(checkStatus, 15 * 1000);
    return () => clearInterval(interval);
  }, [checkStatus]);

  // Calculate ETA — stops countdown when maintenance ends or is ended early
  useEffect(() => {
    if (maintenanceEnded || !info?.end_time) { setEta(''); return; }

    // Use actual_end_time if maintenance was ended early
    const effectiveEnd = info.actual_end_time || info.end_time;

    const updateEta = () => {
      const now = new Date(Date.now() + serverOffsetRef.current);
      const end = new Date(effectiveEnd);
      const diff = end - now;
      if (diff <= 0) {
        // Planned end time passed but system still in maintenance
        setEta("We're finalizing updates. System will be available shortly.");
        return;
      }
      const hrs = Math.floor(diff / (1000 * 60 * 60));
      const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      if (hrs > 0) {
        setEta(`Estimated time remaining: ${hrs}h ${mins}m`);
      } else {
        setEta(`Estimated time remaining: ${mins} minute${mins !== 1 ? 's' : ''}`);
      }
    };

    updateEta();
    const interval = setInterval(updateEta, 30 * 1000);
    return () => clearInterval(interval);
  }, [info?.end_time, info?.actual_end_time, maintenanceEnded]);

  // Determine display state
  let titleText = 'System Under Maintenance';
  let messageText = info?.message || 'We are performing scheduled maintenance to improve your experience. Please check back shortly.';

  if (maintenanceEnded) {
    titleText = 'System is Back Online';
    messageText = 'Maintenance has been completed. Redirecting you now...';
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.icon}>
          {maintenanceEnded ? (
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#28A745" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          ) : (
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#FD7E14" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          )}
        </div>

        <h1 style={styles.title}>{titleText}</h1>

        <p style={styles.message}>{messageText}</p>

        {!maintenanceEnded && info?.title && (
          <div style={styles.detailBox}>
            <strong>{info.title}</strong>
          </div>
        )}

        {!maintenanceEnded && eta && (
          <p style={styles.eta}>{eta}</p>
        )}

        <div style={styles.footer}>
          <p style={styles.footerText}>
            {maintenanceEnded
              ? 'Thank you for your patience.'
              : 'We apologize for the inconvenience. The system will be back online automatically.'}
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#F8F9FA',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    padding: '48px 40px',
    textAlign: 'center',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    maxWidth: '500px',
    width: '100%',
  },
  icon: {
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#212529',
    marginBottom: '16px',
    margin: '0 0 16px',
  },
  message: {
    fontSize: '16px',
    color: '#6C757D',
    lineHeight: 1.6,
    marginBottom: '24px',
  },
  detailBox: {
    backgroundColor: '#FFF3CD',
    border: '1px solid #FFE69C',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '20px',
    fontSize: '14px',
    color: '#664D03',
  },
  eta: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#0D6EFD',
    marginBottom: '24px',
  },
  footer: {
    borderTop: '1px solid #E9ECEF',
    paddingTop: '20px',
    marginTop: '8px',
  },
  footerText: {
    fontSize: '13px',
    color: '#ADB5BD',
    margin: 0,
  },
};

export default Maintenance;
